import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, Check, ChevronRight, CircleCheck, ExternalLink } from "lucide-react";
import { dueText, levelLook } from "../../lib/portalAgenda";
import { Chip } from "./PortalShell";
import Hint from "./Hint";
import GroupPreparation from "./GroupPreparation";

/**
 * The top of the trainer's group page (06.10.2026): where the group is, the
 * next session, and one list of what to do now — late first. Everything
 * comes from `/portal/groups/:id/agenda`.
 */
export default function GroupAgenda({ agenda, groupId, onChanged }) {
  if (!agenda) return null;
  return (
    <>
      <Stages timeline={agenda.timeline} />
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl bg-[#965EA7]/[0.06] px-4 py-3"
        data-testid="portal-agenda-now">
        <p className="min-w-0 flex-1 text-sm text-[#373A45]">
          <span className="font-semibold text-[#7A4986]">Acum: </span>{agenda.now}
        </p>
        {agenda.next_session?.link && (
          <a href={agenda.next_session.link} target="_blank" rel="noreferrer noopener"
            className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-[#7A4986] hover:text-[#965EA7]">
            <CalendarClock size={15} /> Linkul sesiunii <ExternalLink size={13} />
          </a>
        )}
      </div>
      {/* Two steps (08.10.2026): the trainer prepares the group alone first,
          then tells the team, which invites the participants (15.10.2026). */}
      {agenda.preparation && <GroupPreparation groupId={groupId} prep={agenda.preparation} onChanged={onChanged} />}
      <Actions actions={agenda.actions} />
    </>
  );
}

function Stages({ timeline }) {
  return (
    <ol className="mt-6 flex overflow-x-auto pb-1" aria-label="Etapele grupei" data-testid="portal-agenda-stages">
      {timeline.map((t, i) => {
        const done = t.state === "done";
        const current = t.state === "current";
        return (
          <li key={t.key} className="relative min-w-[8.5rem] flex-1" aria-current={current ? "step" : undefined}>
            {i > 0 && (
              <span className={`absolute left-0 right-1/2 top-3 h-0.5 -translate-x-0 ${done || current ? "bg-[#965EA7]" : "bg-[#E5E7EB]"}`}
                aria-hidden="true" />
            )}
            {i < timeline.length - 1 && (
              <span className={`absolute left-1/2 right-0 top-3 h-0.5 ${done ? "bg-[#965EA7]" : "bg-[#E5E7EB]"}`}
                aria-hidden="true" />
            )}
            <div className="relative flex flex-col items-center px-1 text-center">
              <span className={`grid h-6 w-6 place-items-center rounded-full border-2 ${
                current ? "border-[#965EA7] bg-[#965EA7] text-white ring-4 ring-[#965EA7]/15"
                  : done ? "border-[#965EA7] bg-white text-[#965EA7]" : "border-[#D1D5DB] bg-white text-transparent"}`}>
                {done ? <Check size={13} strokeWidth={3} /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
              </span>
              <span className={`mt-2 text-xs font-semibold ${current ? "text-[#7A4986]" : done ? "text-[#373A45]" : "text-[#6B7280]"}`}>
                {t.label}
              </span>
              {t.when && <span className="mt-0.5 text-[11px] text-[#6B7280]">{t.when}</span>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Actions({ actions }) {
  return (
    <section className="mt-8" aria-labelledby="portal-agenda-todo">
      <h2 id="portal-agenda-todo" className="flex items-center gap-1.5 font-display text-xl font-bold text-[#373A45]">
        Ce ai de făcut <Hint term="todo" />
      </h2>
      {actions.length === 0 ? (
        <p className="mt-3 flex items-center gap-2 rounded-2xl border border-[#519872]/25 bg-[#519872]/[0.06] px-4 py-3 text-sm text-[#3c7457]"
          data-testid="portal-agenda-clear">
          <CircleCheck size={17} /> Totul e la zi. Nu ai nimic de făcut acum la grupa asta.
        </p>
      ) : (
        <ul className="mt-3 grid gap-2.5" data-testid="portal-agenda-actions">
          {actions.map((a) => <Action key={a.key} a={a} />)}
        </ul>
      )}
    </section>
  );
}

function Action({ a }) {
  const look = levelLook(a.level);
  const [open, setOpen] = useState(false);
  const items = a.items || [];
  const shown = open ? items : items.slice(0, 3);
  return (
    <li className="relative overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white" data-testid={`portal-agenda-${a.key.split(":")[0]}`}>
      <span className={`absolute inset-y-0 left-0 w-1 ${look.rail}`} aria-hidden="true" />
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3 py-3.5 pl-5 pr-4">
        <div className="min-w-0 flex-1 basis-64">
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-[#373A45]">
            {a.level === "late" && <AlertTriangle size={15} className="shrink-0 text-[#c0483f]" aria-hidden="true" />}
            {a.title}
            {a.level !== "todo" && <Chip tone={look.tone}>{look.label}</Chip>}
          </p>
          {a.detail && <p className="mt-1 text-xs leading-relaxed text-[#6B7280]">{a.detail}</p>}
          {a.due && (
            <p className={`mt-1.5 text-xs font-semibold ${a.level === "late" ? "text-[#a33a32]" : "text-[#373A45]"}`}>
              {a.level === "late" ? "Termenul a trecut: " : "Termen: "}{dueText(a.due)}
            </p>
          )}
        </div>
        {a.href && (
          <Link to={a.href}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#E5E7EB] px-4 py-2 text-sm font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#965EA7]">
            {a.cta || "Deschide"} <ChevronRight size={15} />
          </Link>
        )}
      </div>
      {items.length > 0 && (
        <div className="border-t border-[#F0F1F3] bg-[#FAFAFB] py-1.5 pl-5 pr-4">
          <ul className="divide-y divide-[#F0F1F3]">
            {shown.map((it) => (
              <li key={it.href + it.label}>
                <Link to={it.href} className="flex items-center gap-2 py-1.5 text-xs text-[#373A45] hover:text-[#965EA7]">
                  {it.late && <AlertTriangle size={12} className="shrink-0 text-[#c0483f]" aria-hidden="true" />}
                  <span className="min-w-0 flex-1 truncate">{it.label}</span>
                  <ChevronRight size={13} className="shrink-0 text-[#9CA3AF]" />
                </Link>
              </li>
            ))}
          </ul>
          {items.length > 3 && (
            <button type="button" onClick={() => setOpen((v) => !v)}
              className="py-1 text-xs font-semibold text-[#7A4986] hover:text-[#965EA7]">
              {open ? "Arată mai puține" : `Arată toate (${items.length})`}
            </button>
          )}
        </div>
      )}
    </li>
  );
}
